import type { WebSocketManagerConfig, LogLevel } from './types';
import { WS_TRACKING_EVENTS } from '../constants/nr-events';
import { WebsocketClientType } from '../constants/common';

class WebSocketManager {
  private static instance: WebSocketManager | null = null;
  private static sockets: Map<WebsocketClientType, WebSocket> = new Map();
  private static setupEventListenerCallbacks: Map<string, () => void> =
    new Map();
  private static urls: Map<string, string> = new Map();
  private reconnectAttempts: Map<string, number> = new Map();
  private reconnectInterval: number;
  private maxReconnectAttempts: number;
  private reconnectTimeouts: Map<string, NodeJS.Timeout> = new Map();
  private connectionStartTimes: Map<string, number> = new Map();
  private focusCheckInitialized = false;
  private connectionInProgress: Map<string, boolean> = new Map();
  private static hashToken: string | null = null;
  private static lastFocusTime: number = Date.now();
  private logger?: WebSocketManagerConfig['logger'];
  private analytics?: WebSocketManagerConfig['analytics'];

  constructor(config: WebSocketManagerConfig = {}) {
    if (!WebSocketManager.instance) {
      WebSocketManager.instance = this;
      this.reconnectInterval = config.reconnectInterval ?? 2000;
      this.maxReconnectAttempts = config.maxReconnectAttempts ?? 5;
      this.logger = config.logger;
      this.analytics = config.analytics;
      this.initializeFocusCheck();
    }
  }

  private log(
    level: LogLevel,
    message: string,
    data?: Record<string, unknown>,
  ): void {
    if (this.logger) {
      this.logger.log(level, message, data);
    } else if (level === 'error') {
      console.error(message, data);
    }
  }

  private trackEvent(eventName: string, data: Record<string, unknown>): void {
    if (this.analytics) {
      this.analytics.trackEvent(eventName, data);
    }
  }

  private serializeError(error: unknown): Record<string, unknown> {
    if (error instanceof Error) {
      return {
        name: error.name,
        message: error.message,
        stack: error.stack,
      };
    }
    return { error: String(error) };
  }

  static getHashToken(): string | null {
    return WebSocketManager.hashToken;
  }

  private initializeFocusCheck(): void {
    if (typeof window !== 'undefined' && !this.focusCheckInitialized) {
      window.addEventListener('focus', this.handleWindowFocus.bind(this));
      this.focusCheckInitialized = true;
    }
  }

  private handleWindowFocus(): void {
    this.checkAllConnections();
  }

  private checkAllConnections(): void {
    const timeSinceLastFocus = Date.now() - WebSocketManager.lastFocusTime;
    WebSocketManager.sockets.forEach((socket, socketId) => {
      if (socket.readyState !== WebSocket.OPEN) {
        this.trackEvent(WS_TRACKING_EVENTS.RECONNECTION_ON_FOCUS, {
          client: socketId,
          reconnectAttempt: this.reconnectAttempts.get(socketId) ?? 0,
          maxReconnectAttempts: this.maxReconnectAttempts,
          readyState: socket.readyState,
          isConnectionInProgress:
            this.connectionInProgress.get(socketId) ?? false,
          timeSinceLastFocus,
        });

        if (socket.readyState === WebSocket.CONNECTING) {
          this.log('debug', `Socket ${socketId} is connecting`, {
            timeSinceLastFocus,
          });
          return;
        }

        this.log(
          'debug',
          `Socket ${socketId} is not connected (state: ${socket.readyState}). Attempting to reconnect...`,
          { timeSinceLastFocus },
        );
        this.resetReconnectAttempts(socketId);
        this.handleReconnection(socketId, 'focus check');
      } else {
        this.log(
          'debug',
          `Socket ${socketId} is connected (state: ${socket.readyState})`,
        );
      }
    });
    WebSocketManager.lastFocusTime = Date.now();
  }

  static initialize(hashToken: string | null = null): void {
    WebSocketManager.hashToken = hashToken;
  }

  static getInstance(config?: WebSocketManagerConfig): WebSocketManager {
    if (!this.instance) {
      this.instance = new WebSocketManager(config);
    }
    return this.instance;
  }

  async initializeConnection(
    socketId: WebsocketClientType,
    url: string,
    setupEventListener: () => void,
  ) {
    if (WebSocketManager.sockets.get(socketId)?.readyState === WebSocket.OPEN) {
      return;
    }

    try {
      WebSocketManager.setupEventListenerCallbacks.set(
        socketId,
        setupEventListener,
      );
      WebSocketManager.urls.set(socketId, url);
      await this.connect(socketId, 'initializeConnection');
      this.resetReconnectAttempts(socketId);
    } catch (error: unknown) {
      console.error(`Error initializing socket ${socketId}:`, error);

      this.trackEvent(WS_TRACKING_EVENTS.INITIALIZE_CONNECTION_ERROR, {
        client: socketId,
        url,
        reconnectAttempt: this.reconnectAttempts.get(socketId) ?? 0,
        error: this.serializeError(error),
      });
    }
  }

  private async connect(socketId: WebsocketClientType, source: string) {
    if (this.connectionInProgress.get(socketId)) {
      this.log(
        'debug',
        `Connection already in progress for socket ${socketId}`,
      );
      return;
    }

    try {
      this.connectionInProgress.set(socketId, true);
      this.log('debug', `Connecting socket ${socketId}`, {
        hashToken: !!WebSocketManager.hashToken,
        source,
      });

      this.connectionStartTimes.set(socketId, Date.now());

      const urlBase = WebSocketManager.urls.get(socketId);
      if (!urlBase) {
        throw new Error(`WebSocket URL not initialized for ${socketId}`);
      }
      if (!WebSocketManager.hashToken) {
        throw new Error(`WebSocket auth token not initialized for ${socketId}`);
      }
      const url = `${urlBase}?token=${WebSocketManager.hashToken}`;
      const socket = new WebSocket(url);

      WebSocketManager.sockets.set(socketId, socket);

      const setupEventListener =
        WebSocketManager.setupEventListenerCallbacks.get(socketId);

      if (setupEventListener) {
        setupEventListener();
      }

      this.trackEvent(WS_TRACKING_EVENTS.CONNECT_SUCCESS, {
        client: socketId,
        reconnectAttempt: this.reconnectAttempts.get(socketId) ?? 0,
        url: urlBase,
        source,
      });
    } catch (error: unknown) {
      console.error(
        `Error initializing WebSocket connection ${socketId}:`,
        error,
      );
      WebSocketManager.sockets.delete(socketId);
      this.handleReconnection(socketId, 'connect error');

      this.trackEvent(WS_TRACKING_EVENTS.CONNECT_ERROR, {
        client: socketId,
        reconnectAttempt: this.reconnectAttempts.get(socketId) ?? 0,
        source,
        error: this.serializeError(error),
      });
    } finally {
      this.connectionInProgress.set(socketId, false);
    }
  }

  protected resetReconnectAttempts(socketId: string): void {
    this.reconnectAttempts.set(socketId, 0);
  }

  handleReconnection(socketId: WebsocketClientType, source: string): void {
    const socket = WebSocketManager.sockets.get(socketId);
    const attempts = this.reconnectAttempts.get(socketId) ?? 0;

    if (this.connectionInProgress.get(socketId)) {
      this.trackEvent(WS_TRACKING_EVENTS.RECONNECT_CONNECTION_IN_PROGRESS, {
        client: socketId,
        source,
        readyState: socket ? socket.readyState : 'not initialized',
        reconnectAttempt: attempts,
      });

      return;
    }

    if (this.reconnectTimeouts.get(socketId)) {
      this.log(
        'debug',
        `Reconnect timer already exists for socket ${socketId}`,
        { source },
      );

      this.trackEvent(WS_TRACKING_EVENTS.RECONNECT_TIMER_EXIST, {
        client: socketId,
        source,
        reconnectAttempt: this.reconnectAttempts.get(socketId) ?? 0,
        maxReconnectAttempts: this.maxReconnectAttempts,
      });

      return;
    }

    if (socket?.readyState === WebSocket.OPEN) {
      this.closeSocket(socket, socketId, 4000, 'Reconnecting');
      WebSocketManager.sockets.delete(socketId);

      this.trackEvent(WS_TRACKING_EVENTS.CONNECTION_EXIST_ON_RECONNECT, {
        client: socketId,
        source,
        readyState: socket?.readyState,
        reconnectAttempt: attempts + 1,
        maxReconnectAttempts: this.maxReconnectAttempts,
      });
    }

    if (
      this.maxReconnectAttempts === -1 ||
      attempts < this.maxReconnectAttempts
    ) {
      this.log('debug', `Reconnecting socket ${socketId}...`, {
        attempts,
        maxReconnectAttempts: this.maxReconnectAttempts,
        source,
      });

      const timeout = setTimeout(() => {
        this.reconnectAttempts.set(socketId, attempts + 1);
        void this.connect(socketId, 'reconnect attempt');
        this.reconnectTimeouts.delete(socketId);

        this.trackEvent(WS_TRACKING_EVENTS.RECONNECT_ATTEMPT_TRIGGERED, {
          client: socketId,
          source,
          reconnectAttempt: attempts + 1,
          maxReconnectAttempts: this.maxReconnectAttempts,
        });
      }, this.reconnectInterval);

      this.reconnectTimeouts.set(socketId, timeout);

      this.trackEvent(WS_TRACKING_EVENTS.RECONNECTION_ATTEMPT, {
        client: socketId,
        source,
        reconnectAttempt: attempts,
        maxReconnectAttempts: this.maxReconnectAttempts,
      });
    } else {
      this.trackEvent(WS_TRACKING_EVENTS.MAX_RECONNECTION_ATTEMPTS, {
        client: socketId,
        source,
        reconnectAttempt: attempts,
        maxReconnectAttempts: this.maxReconnectAttempts,
      });
    }
  }

  getSocket(socketId: WebsocketClientType): WebSocket | null {
    return WebSocketManager.sockets.get(socketId) ?? null;
  }

  /**
   * Ensures a socket is in the static Map. Used to restore socket reference
   * after cleanup/HMR clears the Map but the WebSocket connection remains alive.
   */
  protected ensureSocketInMap(
    socketId: WebsocketClientType,
    socket: WebSocket,
  ): void {
    if (!WebSocketManager.sockets.has(socketId)) {
      WebSocketManager.sockets.set(socketId, socket);
    }
  }

  getConnectionCreationTime(socketId: string): number {
    return Date.now() - (this.connectionStartTimes.get(socketId) ?? 0);
  }

  closeSocket(
    socket: WebSocket,
    socketId: string,
    code: number,
    reason: string,
  ) {
    socket.onopen = null;
    socket.onclose = null;
    socket.onmessage = null;
    socket.onerror = null;

    socket.close(code, reason);

    this.connectionStartTimes.delete(socketId);
    this.connectionInProgress.delete(socketId);
  }

  disconnect(socketId: WebsocketClientType) {
    const timeout = this.reconnectTimeouts.get(socketId);
    if (timeout) {
      clearTimeout(timeout);
      this.reconnectTimeouts.delete(socketId);
    }

    const socket = WebSocketManager.sockets.get(socketId);
    if (socket && socket.readyState === WebSocket.OPEN) {
      try {
        this.closeSocket(socket, socketId, 4001, 'Disconnecting');
        this.reconnectAttempts.delete(socketId);
      } catch (error: unknown) {
        console.error(`Error closing WebSocket connection ${socketId}:`, error);

        this.trackEvent(WS_TRACKING_EVENTS.DISCONNECTION_ERROR, {
          client: socketId,
          reconnectAttempt: this.reconnectAttempts.get(socketId) ?? 0,
          connectionState: socket?.readyState,
          error: this.serializeError(error),
        });
      }
    }
    WebSocketManager.sockets.delete(socketId);

    this.trackEvent(WS_TRACKING_EVENTS.DISCONNECTION, {
      client: socketId,
      reconnectAttempt: this.reconnectAttempts.get(socketId) ?? 0,
    });
  }
}

export default WebSocketManager;
